/**
 * Blood Bowl Calculator - Constants
 *
 * Skills, action categories, and risk thresholds translated from the legacy calculator.
 */

import type {
  SkillsMap,
  ActionCategoriesMap,
  RiskThresholdsMap,
  ActionType,
  ActionResult,
  BlockResult,
  InjuryResult,
} from '../types';

// ============================================================================
// Skills
// ============================================================================

export const SKILLS: SkillsMap = {
  dodge: {
    name: 'Dodge',
    nameFr: 'Esquive',
    actions: ['dodge'],
  },
  sure_hands: {
    name: 'Sure Hands',
    nameFr: 'Prise Sûre',
    actions: ['pickup'],
  },
  pass: {
    name: 'Pass',
    nameFr: 'Passe',
    actions: ['pass'],
  },
  catch: {
    name: 'Catch',
    nameFr: 'Réception',
    actions: ['catch'],
  },
  sure_feet: {
    name: 'Sure Feet',
    nameFr: 'Pieds Agiles',
    actions: ['gfi'],
  },
  block: {
    name: 'Block',
    nameFr: 'Block',
    actions: ['block_1d', 'block_2d', 'block_3d', 'block_-2d', 'block_-3d'],
  },
};

// ============================================================================
// Action Categories
// ============================================================================

export const ACTION_CATEGORIES: ActionCategoriesMap = {
  dodge: {
    name: 'Dodge',
    nameFr: 'Esquive',
    results: ['2+', '3+', '4+', '5+', '6+'],
    skill: 'dodge',
  },
  gfi: {
    name: 'GFI',
    nameFr: 'GFI',
    results: ['2+'],
    skill: 'sure_feet',
  },
  pickup: {
    name: 'Pickup',
    nameFr: 'Ramassage',
    results: ['2+', '3+', '4+', '5+', '6+'],
    skill: 'sure_hands',
  },
  pass: {
    name: 'Pass',
    nameFr: 'Passe',
    results: ['2+', '3+', '4+', '5+', '6+'],
    skill: 'pass',
  },
  catch: {
    name: 'Catch',
    nameFr: 'Réception',
    results: ['2+', '3+', '4+', '5+', '6+'],
    skill: 'catch',
  },
  throw_teammate: {
    name: 'Throw Teammate',
    nameFr: 'Lancer coéquipier',
    results: ['2+', '3+', '4+', '5+', '6+'],
    skill: null,
  },
  landing: {
    name: 'Landing',
    nameFr: 'Atterrissage',
    results: ['4+'],
    skill: null,
  },
  block_3d: {
    name: 'Block 3D',
    nameFr: 'Block 3D',
    results: ['pushed_or_pow', 'pow_only', 'pow_no_dodge', 'pow_or_both_down'],
    skill: null,
  },
  block_2d: {
    name: 'Block 2D',
    nameFr: 'Block 2D',
    results: ['pushed_or_pow', 'pow_only', 'pow_no_dodge', 'pow_or_both_down'],
    skill: null,
  },
  block_1d: {
    name: 'Block 1D',
    nameFr: 'Block 1D',
    results: ['pushed_or_pow', 'pow_only', 'pow_no_dodge', 'pow_or_both_down', 'no_skull'],
    skill: null,
  },
  'block_-2d': {
    name: 'Block -2D',
    nameFr: 'Block -2D',
    results: ['pushed_or_pow', 'pow_only', 'pow_no_dodge', 'pow_or_both_down', 'no_skull'],
    skill: null,
  },
  'block_-3d': {
    name: 'Block -3D',
    nameFr: 'Block -3D',
    results: ['pushed_or_pow', 'pow_only', 'pow_no_dodge', 'pow_or_both_down', 'no_skull'],
    skill: null,
  },
  armor: {
    name: 'Armor',
    nameFr: 'Armure',
    results: ['7+', '8+', '9+', '10+'],
    skill: null,
  },
  injury: {
    name: 'Injury',
    nameFr: 'Blessure',
    results: ['stunned_plus', 'ko_plus', 'casualty'],
    skill: null,
  },
  roll: {
    name: 'Simple Roll',
    nameFr: 'Jet simple',
    results: ['2+', '3+', '4+', '5+', '6+'],
    skill: null,
  },
};

// ============================================================================
// Result Labels (French)
// ============================================================================

export const BLOCK_RESULT_LABELS: Record<BlockResult, string> = {
  pushed_or_pow: 'Repoussé ou Pow',
  pow_only: 'Pow uniquement',
  pow_no_dodge: 'Pow sans Esquive',
  pow_or_both_down: 'Pow ou Both Down',
  no_skull: 'Pas de Skull',
};

export const INJURY_RESULT_LABELS: Record<InjuryResult, string> = {
  stunned_plus: 'Sonné+',
  ko_plus: 'KO+',
  casualty: 'Sortie',
};

/**
 * Get the French label for any action result
 */
export function getResultLabel(result: ActionResult): string {
  // Block results
  if (result in BLOCK_RESULT_LABELS) {
    return BLOCK_RESULT_LABELS[result as BlockResult];
  }
  // Injury results
  if (result in INJURY_RESULT_LABELS) {
    return INJURY_RESULT_LABELS[result as InjuryResult];
  }
  // Dice results (2+, 3+, etc.) - return as-is
  return result;
}

/**
 * Get valid results for an action type
 */
export function getResultsForAction(actionType: ActionType): ActionResult[] {
  return ACTION_CATEGORIES[actionType].results;
}

/**
 * Get the default result for an action type
 */
export function getDefaultResult(actionType: ActionType): ActionResult {
  const results = ACTION_CATEGORIES[actionType].results;
  return results[0] as ActionResult;
}

// ============================================================================
// Risk Thresholds
// ============================================================================

export const RISK_THRESHOLDS: RiskThresholdsMap = {
  safe: {
    min: 80,
    color: '#00C853',
    labelFr: 'Sûr',
  },
  reasonable: {
    min: 60,
    color: '#64DD17',
    labelFr: 'Raisonnable',
  },
  risky: {
    min: 40,
    color: '#FFD600',
    labelFr: 'Risqué',
  },
  very_risky: {
    min: 20,
    color: '#FF6D00',
    labelFr: 'Très Risqué',
  },
  desperate: {
    min: 0,
    color: '#DD2C00',
    labelFr: 'Désespéré',
  },
};

/**
 * Get risk info for a given probability percentage
 */
export function getRiskInfo(percentage: number): { level: string; color: string; labelFr: string } {
  if (percentage >= RISK_THRESHOLDS.safe.min) {
    return { level: 'safe', ...RISK_THRESHOLDS.safe };
  }
  if (percentage >= RISK_THRESHOLDS.reasonable.min) {
    return { level: 'reasonable', ...RISK_THRESHOLDS.reasonable };
  }
  if (percentage >= RISK_THRESHOLDS.risky.min) {
    return { level: 'risky', ...RISK_THRESHOLDS.risky };
  }
  if (percentage >= RISK_THRESHOLDS.very_risky.min) {
    return { level: 'very_risky', ...RISK_THRESHOLDS.very_risky };
  }
  return { level: 'desperate', ...RISK_THRESHOLDS.desperate };
}

// ============================================================================
// Reroll Labels
// ============================================================================

export const REROLL_TYPE_LABELS: Record<string, string> = {
  skill: 'Compétence',
  'skill-shared': 'Comp. (partagée)',
  team: 'Équipe',
  none: 'Aucune',
};

// ============================================================================
// Loner Values
// ============================================================================

export const LONER_VALUES = [2, 3, 4, 5] as const;

// ============================================================================
// Team Reroll Limits
// ============================================================================

export const MIN_TEAM_REROLLS = 0;
export const MAX_TEAM_REROLLS = 8;

// ============================================================================
// Player Number Limits
// ============================================================================

export const MIN_PLAYER_NUMBER = 1;
export const MAX_PLAYER_NUMBER = 16;
